import type { LatLng, Pin, PinKind, PinLayer } from "./api";
import { STATUS_TEXT } from "./accounts";
import { type AccountFilter, writeFilter } from "./filter";
import { type LeadView, type Stage, STAGE_TEXT } from "./leads";
import { coordinate } from "./location";

export const MAP_PARAM = "map";
/** Above this zoom each pin shows alone. */
export const CLUSTER_MAX_ZOOM = 15;
export const CLUSTER_CELL_PX = 64;
export const IDLE_MS = 400;

export const readMapMode = (search: string) => new URLSearchParams(search).get(MAP_PARAM) === "1";

/** The search part of the URL with the List or Map choice; other parameters stay. */
export function writeMapMode(search: string, on: boolean): string {
  const params = new URLSearchParams(search);
  if (on) params.set(MAP_PARAM, "1");
  else params.delete(MAP_PARAM);
  const text = params.toString();
  return text ? `?${text}` : "";
}

export const LAYER_PARAM = "layer";

export const readLayer = (search: string): PinLayer => (new URLSearchParams(search).get(LAYER_PARAM) === "tank" ? "tank" : "door");

/** The search part of the URL with the pin layer; the front door is the default and has no parameter. */
export function writeLayer(search: string, layer: PinLayer): string {
  const params = new URLSearchParams(search);
  if (layer === "tank") params.set(LAYER_PARAM, "tank");
  else params.delete(LAYER_PARAM);
  const text = params.toString();
  return text ? `?${text}` : "";
}

export interface Box {
  south: number;
  west: number;
  north: number;
  east: number;
}

export const formatBox = (box: Box) => [box.south, box.west, box.north, box.east].map(coordinate).join(",");

export function parseBox(text: string): Box | null {
  const raw = text.split(",");
  if (raw.length !== 4 || raw.some((part) => !part.trim())) return null;
  const parts = raw.map(Number);
  if (parts.some((value) => !Number.isFinite(value))) return null;
  const [south, west, north, east] = parts;
  if (south > north || Math.abs(south) > 90 || Math.abs(north) > 90 || Math.abs(west) > 180 || Math.abs(east) > 180) return null;
  return { south, west, north, east };
}

export const accountPinFilters = (filter: AccountFilter): Record<string, string> => Object.fromEntries(new URLSearchParams(writeFilter(filter)));

export const leadPinFilters = (view: LeadView, stage: Stage | null): Record<string, string> => ({ view, ...(stage ? { stage } : {}) });

export interface Legend {
  status: string;
  label: string;
  colour: string;
}

const ACCOUNT_COLOURS: Record<string, string> = { NEW: "#5aa2f0", REG: "#4cc38a", CALL: "#e0b13c", HOLD: "#f08a4b", OFF: "#8b95a3" };
const LEAD_COLOURS: Record<string, string> = { lead: "#5aa2f0", qualified: "#3fc1c9", proposal: "#b28cf0", won: "#4cc38a", lost: "#8b95a3" };
export const OTHER_COLOUR = "#d0d7de";
/** A cluster is not a status, so its colour is in no legend. */
export const CLUSTER_COLOUR = "#ffffff";

/** "Group of 7 accounts: zoom in", the hover text of a cluster and its line in the map key. */
export const clusterTitle = (count: number | string, kind: PinKind) => `Group of ${count} ${kind}: zoom in`;

export function legendOf(kind: PinKind): Legend[] {
  const [colours, text] = kind === "accounts" ? [ACCOUNT_COLOURS, STATUS_TEXT as Record<string, string>] : [LEAD_COLOURS, STAGE_TEXT as Record<string, string>];
  return Object.keys(text).map((status) => ({ status, label: text[status], colour: colours[status] ?? OTHER_COLOUR }));
}

export const pinColour = (kind: PinKind, status: string) => (kind === "accounts" ? ACCOUNT_COLOURS : LEAD_COLOURS)[status] ?? OTHER_COLOUR;

export const statusLabel = (kind: PinKind, status: string) =>
  ((kind === "accounts" ? STATUS_TEXT : STAGE_TEXT) as Record<string, string>)[status] ?? status;

/** Rule G-13: a map list line shows the number, the name, the area and the status of the account. */
export function pinListParts(kind: PinKind, pin: Pick<Pin, "number" | "name" | "status" | "area">): { name: string; rest: string[] } {
  const name = [kind === "accounts" ? pin.number : null, pin.name].filter(Boolean).join(" · ") || pin.name;
  const area = kind !== "accounts" || pin.area === undefined ? null : pin.area || "No area";
  return { name, rest: [area, statusLabel(kind, pin.status)].filter((part): part is string => Boolean(part)) };
}

/** The Web Mercator position of a point in pixels at a zoom, as Google Maps draws it. */
export function worldPixel(point: LatLng, zoom: number): { x: number; y: number } {
  const size = 256 * 2 ** zoom;
  const sin = Math.min(Math.max(Math.sin((point.lat * Math.PI) / 180), -0.9999), 0.9999);
  return {
    x: ((point.lng + 180) / 360) * size,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * size,
  };
}

export interface Cluster<T extends LatLng> extends LatLng {
  key: string;
  items: T[];
}

/** Grid clusters: the pins in one square of the screen at this zoom make one cluster at their mean point. */
export function clusterPins<T extends LatLng>(items: T[], zoom: number, cellPx = CLUSTER_CELL_PX, maxZoom = CLUSTER_MAX_ZOOM): Cluster<T>[] {
  const cells = new Map<string, T[]>();
  items.forEach((item, index) => {
    let key = `pin-${index}`;
    if (zoom <= maxZoom) {
      const { x, y } = worldPixel(item, zoom);
      key = `${Math.floor(x / cellPx)}:${Math.floor(y / cellPx)}`;
    }
    const cell = cells.get(key);
    if (cell) cell.push(item);
    else cells.set(key, [item]);
  });
  return [...cells.entries()].map(([key, members]) => ({
    key,
    items: members,
    lat: members.reduce((sum, item) => sum + item.lat, 0) / members.length,
    lng: members.reduce((sum, item) => sum + item.lng, 0) / members.length,
  }));
}

export function pinCountText(kind: PinKind, shown: number, total: number, truncated: boolean): string {
  const noun = kind === "accounts" ? "account" : "lead";
  const plural = (count: number) => `${count.toLocaleString("en-US")} ${noun}${count === 1 ? "" : "s"}`;
  return truncated ? `Showing ${shown.toLocaleString("en-US")} of ${plural(total)} in this view. Zoom in to see all.` : `${plural(total)} in this view`;
}

/** A point that a map can show: finite, in range, and not (0, 0), which is a missing point. */
export const placeable = (point: LatLng) =>
  Number.isFinite(point.lat) && Number.isFinite(point.lng) && Math.abs(point.lat) <= 90 && Math.abs(point.lng) <= 180 && !(point.lat === 0 && point.lng === 0);

/** A point is far when its distance to the median point is more than this times the median distance. */
export const FAR_FACTOR = 8;
/** In degrees. A point nearer than this to the median point is never far. */
export const FAR_MIN_DEG = 1;

const median = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

/** The placeable points without the far points, so that one bad point cannot zoom the map out to the country. */
export function nearPoints<T extends LatLng>(points: T[]): T[] {
  const placed = points.filter(placeable);
  if (placed.length < 3) return placed;
  const lat = median(placed.map((point) => point.lat));
  const lng = median(placed.map((point) => point.lng));
  const scale = Math.cos((lat * Math.PI) / 180);
  const distance = (point: LatLng) => Math.max(Math.abs(point.lat - lat), Math.abs(point.lng - lng) * scale);
  const limit = Math.max(FAR_FACTOR * median(placed.map(distance)), FAR_MIN_DEG);
  return placed.filter((point) => distance(point) <= limit);
}

export interface FirstView {
  /** "fallback" when there are no pins and the other points give the view. */
  source: "pins" | "fallback" | "none";
  points: LatLng[];
  /** The points that the view leaves out: no position, or far. */
  dropped: number;
  /** All points are at one place: the map centres on it at the zoom of one pin. */
  single: boolean;
}

/** The first view of a map: fit the pins; with no pins, fit the fallback points. */
export function firstViewOf(pins: LatLng[], fallback: LatLng[] = []): FirstView {
  const sources: ["pins" | "fallback", LatLng[]][] = [
    ["pins", pins],
    ["fallback", fallback],
  ];
  for (const [source, points] of sources) {
    const near = nearPoints(points);
    if (!near.length) continue;
    const single = near.every((point) => point.lat === near[0].lat && point.lng === near[0].lng);
    return { source, points: near, dropped: points.length - near.length, single };
  }
  return { source: "none", points: [], dropped: 0, single: false };
}

/** How far the first view is: none yet, the fallback points while the pins load, or final. */
export type FitDone = "none" | "fallback" | "final";

export type FitStep = { action: "fit"; view: FirstView; done: FitDone } | { action: "keep" } | { action: "wait" };

/** The next step of the first view: fit the fallback at once, then the pins if the user did not move the map. */
export function firstFitStep(done: FitDone, loaded: boolean, moved: boolean, pins: LatLng[], fallback: LatLng[]): FitStep {
  if (done === "final") return { action: "wait" };
  if (!loaded) {
    const early = firstViewOf([], fallback);
    return done === "none" && !moved && early.source === "fallback" ? { action: "fit", view: early, done: "fallback" } : { action: "wait" };
  }
  if (moved) return { action: "keep" };
  const view = firstViewOf(pins, fallback);
  if (view.source === "none") return { action: "wait" };
  if (done === "fallback" && view.source === "fallback") return { action: "keep" };
  return { action: "fit", view, done: "final" };
}
